import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun, Table, TableRow, TableCell, WidthType, BorderStyle, Header, Footer, PageNumber, LevelFormat, convertInchesToTwip, VerticalAlignTable } from "docx";
import { PDFDocument, rgb, type PDFImage } from "pdf-lib";
import { TextKit } from "./pdfText";
import { downloadBlob } from "@/components/ToolLayout";
import { toast } from "sonner";

export interface DocSection {
  heading?: string;
  paragraphs: string[];
  /** Optional bulleted list rendered after this section's paragraphs --
   *  added for documents that need a real list (e.g. "what you'll gain
   *  during this internship"), without changing anything for the many
   *  existing callers that never set it. */
  bullets?: string[];
  /** Optional signature image rendered after this section's content --
   *  belongs on DocSection rather than as a single always-at-the-end
   *  document option, since a real signature image needs to sit exactly
   *  where the caller's "Sincerely," paragraph is, not wherever the
   *  document happens to end (which, once an acceptance/countersignature
   *  section exists after the closing signature, is the wrong place). */
  signatureImage?: SignatureImageOptions;
}

/** A boxed letterhead -- logo on the left, company name/tagline lines on
 *  the right, inside a bordered box -- rendered above the title when
 *  provided. Entirely optional: every existing caller that doesn't pass
 *  this gets the exact same plain centered-title output as before. */
export interface LetterheadOptions {
  logoBytes?: Uint8Array;
  logoType?: "png" | "jpg";
  companyLine1: string;
  companyLine2?: string;
}

/** An optional signature image (a scanned/drawn signature, or a
 *  generated cursive-style one) rendered above the signer's typed name
 *  in the closing signature block, in place of a blank line. */
export interface SignatureImageOptions {
  imageBytes: Uint8Array;
  imageType: "png" | "jpg";
}

// Word's built-in heading styles (Title, Heading 2) come with their own
// default sizes that vary by template/theme -- explicit sizes are set
// here instead so every generated document is consistently 16pt title /
// 14pt section headings / 12pt body, regardless of Word's own defaults.
// The docx library's `size` is in half-points (confirmed directly against
// the actual generated XML): 32 = 16pt, 28 = 14pt, 24 = 12pt.
const TITLE_SIZE = 32;
const HEADING_SIZE = 28;
const BODY_SIZE = 24;

const BULLET_NUMBERING_REFERENCE = "legal-doc-bullets";

/**
 * Builds and downloads a simple, cleanly formatted .docx from a title +
 * sections. `subtitle` is optional -- when given, it renders directly
 * under the title in the same subheading style/size used for section
 * headings, for documents that want a two-line heading (e.g. a company
 * name as the main heading with "Offer Letter" as the subheading).
 *
 * `letterhead`, `signatureImage`, and `footerText` are later additions,
 * all optional -- every one of the 8 tools already using this function
 * gets byte-for-byte the same output as before if it doesn't pass them.
 */
export async function generateLegalDoc(
  title: string,
  sections: DocSection[],
  filename: string,
  subtitle?: string,
  options?: { letterhead?: LetterheadOptions; footerText?: string }
) {
  const children: (Paragraph | Table)[] = [];

  children.push(
    new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: TITLE_SIZE })],
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: subtitle ? 60 : 300 },
    })
  );

  if (subtitle) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: subtitle, bold: true, size: HEADING_SIZE })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 },
      })
    );
  }

  for (const section of sections) {
    if (section.heading) {
      children.push(
        new Paragraph({
          // Word's built-in Heading 2 style carries its own theme color
          // (usually a blue) that would otherwise override any color
          // choice made here -- explicit black keeps every generated
          // document's headings visually consistent with its body text,
          // regardless of which Word theme opens it.
          children: [new TextRun({ text: section.heading, bold: true, size: HEADING_SIZE, color: "1A1A1A" })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 },
        })
      );
    }
    for (const para of section.paragraphs) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: para, size: BODY_SIZE })],
          spacing: { after: 150 },
        })
      );
    }
    if (section.bullets) {
      for (const bullet of section.bullets) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: bullet, size: BODY_SIZE })],
            numbering: { reference: BULLET_NUMBERING_REFERENCE, level: 0 },
            spacing: { after: 80 },
          })
        );
      }
    }
    // Rendered right where the caller placed it in the sections array --
    // typically the section containing the "Sincerely," paragraph --
    // rather than unconditionally at the very end of the whole document,
    // which put it after a countersignature/acceptance block on any
    // letter that has one.
    if (section.signatureImage) {
      children.push(
        new Paragraph({
          spacing: { before: 100, after: 40 },
          children: [new ImageRun({ type: section.signatureImage.imageType, data: section.signatureImage.imageBytes, transformation: { width: 140, height: 55 } })],
        })
      );
    }
  }

  // A real Word header, not body content -- repeats on every page
  // automatically, matching what the PDF sibling function already does
  // by redrawing it per page. An earlier version put the letterhead as
  // ordinary content at the top of the document, which meant it only
  // ever appeared on page 1 of anything that ran longer than a page.
  const headerChildren: (Paragraph | Table)[] = [];
  if (options?.letterhead) {
    const lh = options.letterhead;
    const logoCellChildren: Paragraph[] = [];
    if (lh.logoBytes && lh.logoType) {
      logoCellChildren.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [new ImageRun({ type: lh.logoType, data: lh.logoBytes, transformation: { width: 50, height: 50 } })],
        })
      );
    }
    const infoCellChildren = [
      new Paragraph({ children: [new TextRun({ text: lh.companyLine1, bold: true, size: HEADING_SIZE })] }),
    ];
    if (lh.companyLine2) {
      infoCellChildren.push(new Paragraph({ children: [new TextRun({ text: lh.companyLine2, size: 18, color: "555555" })] }));
    }
    headerChildren.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: [1400, 8000],
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: "222222" },
          bottom: { style: BorderStyle.SINGLE, size: 4, color: "222222" },
          left: { style: BorderStyle.SINGLE, size: 4, color: "222222" },
          right: { style: BorderStyle.SINGLE, size: 4, color: "222222" },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
          insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({ width: { size: 1400, type: WidthType.DXA }, children: logoCellChildren.length ? logoCellChildren : [new Paragraph("")], verticalAlign: VerticalAlignTable.CENTER }),
              new TableCell({ width: { size: 8000, type: WidthType.DXA }, children: infoCellChildren, verticalAlign: VerticalAlignTable.CENTER }),
            ],
          }),
        ],
      })
    );
    headerChildren.push(new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: "0D9488" } }, spacing: { after: 100 }, children: [] }));
  }

  const doc = new Document({
    numbering: { config: [{ reference: BULLET_NUMBERING_REFERENCE, levels: [{ level: 0, format: LevelFormat.BULLET, text: "\u2022", alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: convertInchesToTwip(0.4), hanging: convertInchesToTwip(0.2) } } } }] }] },
    sections: [
      {
        headers: headerChildren.length ? { default: new Header({ children: headerChildren }) } : undefined,
        footers: options?.footerText
          ? {
              default: new Footer({
                children: [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    border: { top: { style: BorderStyle.SINGLE, size: 4, color: "CCCCCC" } },
                    spacing: { before: 120 },
                    children: [
                      new TextRun({ text: options.footerText + " | Page ", size: 16, color: "888888" }),
                      new TextRun({ children: [PageNumber.CURRENT], size: 16, color: "888888" }),
                    ],
                  }),
                ],
              }),
            }
          : undefined,
        children,
      },
    ],
  });
  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, filename);
}

export function today(): string {
  return longDate(new Date());
}

/** "October 23, 2026" for a Date or a YYYY-MM-DD string: one long date format for every document tool. */
export function longDate(d: Date | string): string {
  if (typeof d === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return d;
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" });
  }
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * PDF sibling of generateLegalDoc -- same title/sections/filename shape,
 * so callers can offer both formats from the same data with no
 * duplication. Basic but genuinely paginated: text wraps to the page
 * width and flows onto new pages as needed, rather than clipping or
 * overlapping when a document runs long.
 *
 * `letterhead` and `footerText`, when provided, are redrawn on every
 * page (not just the first) -- a real multi-page letter needs its
 * letterhead and footer on each page, not only where they first
 * appeared. `signatureImage` behaves the same as the docx version.
 */
export async function generateLegalDocPdf(
  title: string,
  sections: DocSection[],
  filename: string,
  subtitle?: string,
  options?: { letterhead?: LetterheadOptions; footerText?: string }
) {
  const pdf = await PDFDocument.create();
  const everything = [title, subtitle ?? "", options?.footerText ?? "", options?.letterhead?.companyLine1 ?? "", options?.letterhead?.companyLine2 ?? "",
    ...sections.flatMap((x) => [x.heading ?? "", ...x.paragraphs, ...(x.bullets ?? [])])].join("\n");
  const kit = await TextKit.create(pdf, everything);

  const pageWidth = 612; // US Letter, points
  const pageHeight = 792;
  const margin = 64;
  const maxWidth = pageWidth - margin * 2;
  const footerHeight = options?.footerText ? 30 : 0;

  let logoImage: PDFImage | null = null;
  if (options?.letterhead?.logoBytes && options.letterhead.logoType) {
    logoImage = options.letterhead.logoType === "png" ? await pdf.embedPng(options.letterhead.logoBytes) : await pdf.embedJpg(options.letterhead.logoBytes);
  }
  // Each section's signature image (if any) is embedded once here and
  // cached by section index, then drawn inline during the main section
  // loop below -- same reasoning as the docx version: it needs to render
  // exactly where the caller placed it (after "Sincerely,"), not
  // unconditionally at the very end of the document.
  const sectionSignatureImages = await Promise.all(
    sections.map((s) => (s.signatureImage ? (s.signatureImage.imageType === "png" ? pdf.embedPng(s.signatureImage.imageBytes) : pdf.embedJpg(s.signatureImage.imageBytes)) : Promise.resolve(null)))
  );

  const drawLetterhead = (page: import("pdf-lib").PDFPage) => {
    if (!options?.letterhead) return pageHeight - margin;
    const lh = options.letterhead;
    const boxTop = pageHeight - margin + 20;
    const boxHeight = 56;
    const boxBottom = boxTop - boxHeight;
    page.drawRectangle({ x: margin, y: boxBottom, width: maxWidth, height: boxHeight, borderColor: rgb(0.13, 0.13, 0.13), borderWidth: 1 });
    let textX = margin + 14;
    if (logoImage) {
      const logoSize = 40;
      page.drawImage(logoImage, { x: margin + 8, y: boxBottom + (boxHeight - logoSize) / 2, width: logoSize, height: logoSize });
      textX = margin + 8 + logoSize + 12;
    }
    kit.draw(page, lh.companyLine1, { x: textX, y: boxBottom + boxHeight - 22, size: 14, bold: true });
    if (lh.companyLine2) {
      kit.draw(page, lh.companyLine2, { x: textX, y: boxBottom + boxHeight - 40, size: 9, color: rgb(0.4, 0.4, 0.4) });
    }
    page.drawRectangle({ x: margin, y: boxBottom - 4, width: maxWidth, height: 3, color: rgb(0.051, 0.588, 0.533) }); // teal accent rule, matches brand
    return boxBottom - 22;
  };

  const drawFooter = (page: import("pdf-lib").PDFPage, pageNum: number) => {
    if (!options?.footerText) return;
    const text = `${options.footerText} | Page ${pageNum}`;
    const size = 8;
    const width = kit.width(text, size);
    page.drawLine({ start: { x: margin, y: margin - 6 }, end: { x: pageWidth - margin, y: margin - 6 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    kit.draw(page, text, { x: (pageWidth - width) / 2, y: margin - 18, size, color: rgb(0.55, 0.55, 0.55) });
  };

  let pageNum = 1;
  let page = pdf.addPage([pageWidth, pageHeight]);
  let y = drawLetterhead(page);
  drawFooter(page, pageNum);

  const newPage = () => {
    pageNum++;
    page = pdf.addPage([pageWidth, pageHeight]);
    y = drawLetterhead(page);
    drawFooter(page, pageNum);
  };

  const ensureSpace = (needed: number) => {
    if (y - needed < margin + footerHeight) newPage();
  };

  const drawLine = (text: string, size: number, useBold: boolean, gapAfter: number, center = false) => {
    ensureSpace(size + gapAfter);
    const width = kit.width(text, size, useBold);
    const x = center ? (pageWidth - width) / 2 : margin;
    kit.draw(page, text, { x, y, size, bold: useBold });
    y -= size + gapAfter;
  };

  drawLine(title, 20, true, subtitle ? 6 : 20, true);
  if (subtitle) drawLine(subtitle, 14, true, 20, true);

  sections.forEach((section, idx) => {
    if (section.heading) drawLine(section.heading, 13, true, 8);
    for (const para of section.paragraphs) {
      const lines = kit.wrap(para, 11, maxWidth);
      for (const line of lines) drawLine(line, 11, false, 4);
      y -= 6; // small extra gap between paragraphs within a section
    }
    if (section.bullets) {
      for (const bullet of section.bullets) {
        const lines = kit.wrap(bullet, 11, maxWidth - 18);
        lines.forEach((line, i) => {
          ensureSpace(11 + 4);
          const prefix = i === 0 ? "\u2022  " : "   ";
          kit.draw(page, prefix + line, { x: margin, y, size: 11 });
          y -= 15;
        });
      }
      y -= 4;
    }
    const sigImg = sectionSignatureImages[idx];
    if (sigImg) {
      const sigW = 110, sigH = 44;
      ensureSpace(sigH + 10);
      page.drawImage(sigImg, { x: margin, y: y - sigH, width: sigW, height: sigH });
      y -= sigH + 6;
    }
  });

  await kit.finish();
  const bytes = await pdf.save();
  const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
  downloadBlob(blob, filename);
  const note = kit.note();
  if (note) toast.warning(note);
  return { note };
}
